import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Listing, Coupon } from '../types';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { 
  ShieldCheck, Zap, Lock, CreditCard, Tag, 
  Check, AlertCircle, ArrowRight, ArrowLeft 
} from 'lucide-react';
import { WarrantyBadge } from '../components/common/Badge';

export const Checkout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { displayPrice } = useCurrency();

  const state = (location.state || {}) as {
    listing_id?: string;
    quantity?: number;
    boosting_details?: any;
    customPriceCents?: number;
  };

  const [listing, setListing] = useState<Listing | null>(null);
  const [quantity, setQuantity] = useState(state.quantity || 1);
  const [buyerNotes, setBuyerNotes] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [couponDiscountCents, setCouponDiscountCents] = useState(0);
  const [useStoreCredit, setUseStoreCredit] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!state.listing_id) {
      navigate('/browse');
      return;
    }

    const fetchListing = async () => {
      try {
        const res = await fetch(`/api/listings/${state.listing_id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.listing) {
            setListing(data.listing);
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchListing();
  }, [state.listing_id]);

  if (!listing) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center text-xs">
        Loading checkout details...
      </div>
    );
  }

  // Base pricing
  const unitPrice = state.customPriceCents || listing.price_cents;
  const subtotalCents = unitPrice * quantity;

  // Coupon application handler
  const handleApplyCoupon = () => {
    setErrorMsg('');
    const code = couponCode.toUpperCase().trim();
    if (code === 'WELCOME10') {
      const discount = Math.round(subtotalCents * 0.10);
      setCouponDiscountCents(discount);
      setAppliedCoupon('WELCOME10');
    } else if (code === 'VAULT5') {
      setCouponDiscountCents(Math.min(subtotalCents, 500));
      setAppliedCoupon('VAULT5');
    } else if (code === 'BOOST20' && listing.category_slug === 'boosting') {
      const discount = Math.round(subtotalCents * 0.20);
      setCouponDiscountCents(discount);
      setAppliedCoupon('BOOST20');
    } else {
      setErrorMsg('Invalid or expired coupon code. Try WELCOME10 or VAULT5');
    }
  };

  // Store credit calculation
  const availableCreditCents = user?.store_credit_cents || 0;
  const remainingAfterCoupon = Math.max(0, subtotalCents - couponDiscountCents);
  const creditAppliedCents = useStoreCredit ? Math.min(remainingAfterCoupon, availableCreditCents) : 0;
  const finalTotalCents = Math.max(0, remainingAfterCoupon - creditAppliedCents);

  const priceInfo = displayPrice(finalTotalCents);

  const handleCompletePayment = async () => {
    if (!user) {
      setErrorMsg('Please log in or select a demo account from the top bar before completing checkout.');
      return;
    }
    if (!termsAccepted) {
      setErrorMsg('Please accept the GameVault Terms of Service and Buyer Protection Policy to proceed.');
      return;
    }

    setProcessing(true);
    setErrorMsg('');

    try {
      // Step 1: Create Order in Cloudflare D1
      const orderRes = await fetch('/api/orders/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listing.id,
          quantity,
          coupon_code: appliedCoupon,
          use_credit_cents: creditAppliedCents,
          buyer_notes: buyerNotes,
          boosting_details: state.boosting_details
        })
      });

      const orderData = await orderRes.json();
      if (!orderRes.ok || !orderData.success) {
        throw new Error(orderData.error || 'Failed to create order');
      }

      const orderId = orderData.orderId;

      // Step 2: Process payment via Sandbox Provider Adapter
      const idempotencyKey = `idemp_${orderId}_${Date.now()}`;
      const payRes = await fetch('/api/payments/process-sandbox', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          idempotency_key: idempotencyKey
        })
      });

      const payData = await payRes.json();
      if (!payRes.ok || !payData.success) {
        throw new Error(payData.error || 'Payment failed');
      }

      // Step 3: Redirect to post-payment status page
      navigate(`/checkout/status/${orderId}`);
    } catch (err: any) {
      setErrorMsg(err.message || 'Payment processing failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Back button & title */}
      <div>
        <Link to={`/product/${listing.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-lightSecText hover:text-brand-blue mb-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Offer
        </Link>
        <h1 className="text-3xl font-black text-brand-lightText dark:text-brand-darkText">
          Complete Your Order
        </h1>
        <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
          Review item details, apply coupons or credits, and finalize with GameVault Sandbox Checkout
        </p>
      </div>

      {errorMsg && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-400 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left 2 Cols: Form & Delivery Fields */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Order Item Summary Box */}
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder flex gap-4 items-center">
            <img
              src={(listing.images && listing.images[0]) || 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=200'}
              alt=""
              className="w-20 h-20 rounded-2xl object-cover bg-slate-900 border border-brand-lightBorder dark:border-brand-darkBorder flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <span className="text-[11px] uppercase font-bold text-brand-blue dark:text-brand-accent">
                {listing.game_name} • {listing.category_slug}
              </span>
              <h3 className="font-bold text-sm text-brand-lightText dark:text-brand-darkText truncate">
                {listing.title}
              </h3>
              <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
                Seller: <strong>{listing.store_name || 'Verified Merchant'}</strong> • Delivery: <strong className="capitalize">{listing.delivery_type}</strong>
              </p>
            </div>
            <WarrantyBadge hours={listing.warranty_hours || 48} />
          </div>

          {/* Delivery & Contact Information */}
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
            <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText">
              Delivery Instructions
            </h3>

            {listing.delivery_type === 'automatic' ? (
              <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-3">
                <Zap className="w-5 h-5 flex-shrink-0 text-amber-500" />
                <div>
                  <p className="font-bold">Instant Automatic Key Delivery</p>
                  <p className="mt-0.5">Your decrypted access codes or login credentials will be displayed immediately upon confirmed payment on the next screen.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText">
                  Buyer Notes / In-Game Username / Riot ID / Steam Trade URL:
                </label>
                <textarea
                  rows={3}
                  value={buyerNotes}
                  onChange={(e) => setBuyerNotes(e.target.value)}
                  placeholder="Provide your in-game name, server, or trade URL for the seller to coordinate delivery..."
                  className="w-full p-3 rounded-2xl text-xs bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>
            )}
          </div>

          {/* Explicitly Labeled Sandbox Provider Selector */}
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-brand-blue dark:text-brand-accent" />
                <span>Payment Provider</span>
              </h3>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
                Sandbox Mode Active
              </span>
            </div>

            <div className="p-4 rounded-2xl border-2 border-brand-blue bg-brand-blue/5 dark:bg-brand-blue/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-blue text-white flex items-center justify-center font-bold">
                  SBX
                </div>
                <div>
                  <h4 className="font-bold text-xs text-brand-lightText dark:text-brand-darkText">GameVault Test Sandbox Gateway</h4>
                  <p className="text-[11px] text-brand-lightSecText dark:text-brand-darkSecText">Exercises server validation, D1 stock reservation, and ledger updates</p>
                </div>
              </div>
              <Check className="w-5 h-5 text-brand-blue" />
            </div>

            <p className="text-[11px] text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed">
              <strong>Notice:</strong> This is an explicitly simulated sandbox environment. No actual bank or credit card charge will occur.
            </p>
          </div>
        </div>

        {/* Right Col: Price Breakdown & Coupon */}
        <div className="space-y-6">
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-xl space-y-6">
            <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText">
              Order Summary
            </h3>

            {/* Price Calculations */}
            <div className="space-y-3 text-xs border-b border-brand-lightBorder dark:border-brand-darkBorder pb-4">
              <div className="flex justify-between">
                <span className="text-brand-lightSecText dark:text-brand-darkSecText">Subtotal ({quantity} item{quantity > 1 ? 's' : ''}):</span>
                <span className="font-bold text-brand-lightText dark:text-brand-darkText">${(subtotalCents / 100).toFixed(2)} USD</span>
              </div>

              {couponDiscountCents > 0 && (
                <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
                  <span>Coupon ({appliedCoupon}):</span>
                  <span>-${(couponDiscountCents / 100).toFixed(2)} USD</span>
                </div>
              )}

              {creditAppliedCents > 0 && (
                <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
                  <span>Store Credit Applied:</span>
                  <span>-${(creditAppliedCents / 100).toFixed(2)} USD</span>
                </div>
              )}

              <div className="flex justify-between text-brand-lightSecText dark:text-brand-darkSecText">
                <span>Vault Protection & Processing:</span>
                <span className="text-emerald-600 font-bold">FREE</span>
              </div>
            </div>

            {/* Coupon Code Input */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText">
                Promo Code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="e.g. WELCOME10"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="flex-1 px-3 py-2 text-xs rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder uppercase font-mono text-brand-lightText dark:text-brand-darkText"
                />
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  className="px-3.5 py-2 rounded-xl text-xs font-bold text-brand-blue dark:text-brand-accent bg-brand-blue/10 hover:bg-brand-blue/20 transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>

            {/* Store Credit Option */}
            {user && availableCreditCents > 0 && (
              <label className="flex items-center justify-between p-3 rounded-xl border border-brand-lightBorder dark:border-brand-darkBorder bg-brand-lightSec/50 dark:bg-brand-darkElevated/50 cursor-pointer">
                <div>
                  <p className="text-xs font-bold text-brand-lightText dark:text-brand-darkText">Apply Store Credit</p>
                  <p className="text-[11px] text-brand-lightSecText">Balance: ${(availableCreditCents / 100).toFixed(2)} USD</p>
                </div>
                <input
                  type="checkbox"
                  checked={useStoreCredit}
                  onChange={(e) => setUseStoreCredit(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue"
                />
              </label>
            )}

            {/* Total */}
            <div className="pt-2 border-t border-brand-lightBorder dark:border-brand-darkBorder">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <span className="text-xs text-brand-lightSecText dark:text-brand-darkSecText block">Total to Pay</span>
                  <span className="text-[10px] text-brand-lightSecText">{priceInfo.chargedUsd}</span>
                </div>
                <span className="font-black text-3xl text-brand-blue dark:text-brand-accent">
                  {priceInfo.formatted}
                </span>
              </div>

              {/* Terms Acceptance Checkbox */}
              <label className="flex items-start gap-2 text-[11px] text-brand-lightSecText dark:text-brand-darkSecText cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue mt-0.5 flex-shrink-0"
                />
                <span>
                  I agree to the <Link to="/legal?tab=terms" target="_blank" className="underline text-brand-blue">Terms of Service</Link> and acknowledge the <Link to="/legal?tab=refund" target="_blank" className="underline text-brand-blue">72-Hour Buyer Protection Policy</Link>.
                </span>
              </label>

              {/* Pay Button */}
              <button
                onClick={handleCompletePayment}
                disabled={processing}
                className="w-full py-4 rounded-2xl font-extrabold text-sm text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              >
                <Lock className="w-4 h-4" />
                <span>{processing ? 'Processing Server Payment...' : `Confirm & Pay ${priceInfo.formatted}`}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
